/* ==========================================================================
   ZELICAT THEME — collection.js
   Instant filtering, sorting and pagination for the collection page.
   Progressive enhancement: without this file, filters are a normal GET form and every chip / page number is a plain link.
   With it, the product area is refreshed through the Section Rendering API (?section_id=…), the address bar and history are
   updated, keyboard focus is kept, and the result count is announced to screen readers.
   Also ports the mockup behaviour of the filter panel: always open on desktop, collapsed (toggle) on phones/tablets.
   ========================================================================== */
(() => {
  'use strict';
  const ZC = window.ZC;
  if (!ZC) return;
  const { $, $$ } = ZC;

  const DESKTOP = window.matchMedia('(min-width: 900px)');

  function initCollection(section) {
    if (!ZC.once(section, 'collection')) return;
    const sectionId = section.getAttribute('data-section-id');
    const live = $('[data-zc-live]', section);
    const cache = new Map();
    let controller = null;
    let lastUrl = location.pathname + location.search;

    const getRoot = () => $('[data-zc-root]', section);

    /* ---------------------------------------------------------------- filter panel (desktop always open, phones collapsible) */
    const syncDetails = (details, keepOpen) => {
      if (!details) return;
      if (DESKTOP.matches) details.setAttribute('open', '');
      else if (keepOpen === true) details.setAttribute('open', '');
      else details.removeAttribute('open');
      const summary = $('.filters__title', details);
      if (summary) summary.setAttribute('aria-expanded', String(details.open));
    };
    const bindDetails = (details) => {
      if (!details || !ZC.once(details, 'filters')) return;
      details.addEventListener('toggle', () => {
        const summary = $('.filters__title', details);
        if (summary) summary.setAttribute('aria-expanded', String(details.open));
      });
    };
    const initDetails = () => {
      const details = $('[data-filters]', section);
      bindDetails(details);
      syncDetails(details, false);
    };
    const onBreakpoint = () => {
      if (!section.isConnected) return;
      const details = $('[data-filters]', section);
      syncDetails(details, !!(details && details.open));
    };
    if (DESKTOP.addEventListener) DESKTOP.addEventListener('change', onBreakpoint);
    else DESKTOP.addListener(onBreakpoint);
    section.addEventListener('click', (e) => {
      const summary = e.target.closest('.filters__title');
      if (summary && DESKTOP.matches) e.preventDefault();
    });
    initDetails();

    /* ---------------------------------------------------------------- helpers */
    const cleanUrl = (href) => {
      const u = new URL(href, location.origin);
      u.searchParams.delete('section_id');
      return u.pathname + u.search;
    };
    const cleanLinks = (scope) => {
      $$('a[href*="section_id"]', scope).forEach((a) => a.setAttribute('href', cleanUrl(a.getAttribute('href'))));
    };
    const formToUrl = (form) => {
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (typeof value === 'string' && value !== '') params.append(key, value);
      });
      const path = new URL(form.getAttribute('action') || location.pathname, location.origin).pathname;
      const qs = params.toString();
      return qs ? path + '?' + qs : path;
    };
    const attrEscape = (v) => String(v).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

    const announce = (text) => {
      if (!live) return;
      live.textContent = '';
      window.setTimeout(() => { live.textContent = text; }, 60);
    };

    /* ---------------------------------------------------------------- fetch + swap */
    const fetchHtml = async (url) => {
      if (cache.has(url)) return cache.get(url);
      const req = new URL(url, location.origin);
      req.searchParams.set('section_id', sectionId);
      if (controller) controller.abort();
      controller = new AbortController();
      const res = await fetch(req.pathname + req.search, { signal: controller.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error('bad response');
      const html = await res.text();
      cache.set(url, html);
      return html;
    };

    const swap = (html, opts) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('[data-zc-collection] [data-zc-root]', doc) || $('[data-zc-root]', doc);
      const current = getRoot();
      if (!fresh || !current) return false;

      const active = document.activeElement;
      const hadFocus = active && current.contains(active);
      const focusKey = hadFocus && active.closest('[data-zc-key]') ? active.closest('[data-zc-key]').getAttribute('data-zc-key') : null;
      const oldDetails = $('[data-filters]', current);
      const wasOpen = !!(oldDetails && oldDetails.open);

      current.replaceWith(fresh);
      cleanLinks(fresh);
      $$('.reveal, .reveal-img', fresh).forEach((el) => el.classList.add('is-in'));

      const details = $('[data-filters]', fresh);
      bindDetails(details);
      syncDetails(details, wasOpen);

      if (hadFocus) {
        let target = focusKey ? $('[data-zc-key="' + attrEscape(focusKey) + '"]', fresh) : null;
        if (target && (target.disabled || target.hidden)) target = null;
        if (!target) { fresh.setAttribute('tabindex', '-1'); target = fresh; }
        target.focus({ preventScroll: true });
      }
      const count = $('[data-zc-count]', fresh);
      announce(count ? count.textContent.trim() : '');
      if (opts && opts.scroll) {
        const top = section.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: Math.max(0, top - 24), behavior: ZC.reduceMotion ? 'auto' : 'smooth' });
      }
      return true;
    };

    const update = async (url, opts = {}) => {
      const target = cleanUrl(url);
      const root = getRoot();
      if (root) { root.classList.add('is-loading'); root.setAttribute('aria-busy', 'true'); }
      try {
        const html = await fetchHtml(target);
        if (!swap(html, opts)) { window.location.href = target; return; }
        if (opts.push !== false) history.pushState({ zcCollection: true }, '', target);
        lastUrl = location.pathname + location.search;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        window.location.href = target;
      }
    };

    /* ---------------------------------------------------------------- events */
    section.addEventListener('change', (e) => {
      const el = e.target;
      const form = el.form || el.closest('[data-zc-filter-form]');
      if (!form || !form.matches('[data-zc-filter-form]') || !section.contains(form)) return;
      update(formToUrl(form));
    });
    section.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-zc-filter-form]');
      if (!form) return;
      e.preventDefault();
      update(formToUrl(form));
    });
    section.addEventListener('click', (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest('a[href]');
      if (!a || !section.contains(a)) return;
      if (!a.matches('[data-zc-applied] a, [data-zc-chips] a, [data-zc-pagination] a, .filters__clear, [data-zc-nav]')) return;
      e.preventDefault();
      update(a.getAttribute('href'), { scroll: !!a.closest('[data-zc-pagination]') });
    });
    window.addEventListener('popstate', () => {
      if (!section.isConnected) return;
      const now = location.pathname + location.search;
      if (now === lastUrl) return;
      update(now, { push: false });
    });
  }

  ZC.mount((root) => {
    $$('[data-zc-collection]', root).forEach(initCollection);
    if (root.matches && root.matches('[data-zc-collection]')) initCollection(root);
  });
})();
